import { z } from 'zod';
import type {
	CreatePricingPolicyInput,
	UpdatePricingPolicyInput
} from '@/graphql/generated';

const nonEmptyString = (field: string) =>
	z
		.string({ required_error: `${field} is required` })
		.trim()
		.min(1, `${field} is required`);

const ULID_REGEX = /^[0123456789ABCDEFGHJKMNPQRSTVWXYZ]{26}$/i;

export const pricingPolicyFormSchema = z.object({
        name: nonEmptyString('Name'),
        priority: z
                .string({ required_error: 'Priority is required' })
                .trim()
                .regex(/^[0-9]+$/, 'Priority must be a whole number'),
        isActive: z.boolean(),
        scenarioId: z.string().trim().optional(),
        tariffId: nonEmptyString('Tariff'),
	overstayTariffId: z
		.string()
		.trim()
		.refine(
			value =>
				value.length === 0 || ULID_REGEX.test(value),
			'Overstay tariff must be a valid ULID.'
		),
        limitId: z.string().trim().optional(),
        billingCycleId: nonEmptyString('Billing cycle')
});

export type PricingPolicyFormValues = z.infer<typeof pricingPolicyFormSchema>;

const parseRequiredInteger = (value: string, fieldName: string): number => {
	const parsed = Number.parseInt(value, 10);

	if (Number.isNaN(parsed)) {
		throw new Error(`${fieldName} is invalid.`);
	}

	return parsed;
};

const sanitiseName = (value: string) => value.trim();

export const buildCreatePricingPolicyInput = (
	values: PricingPolicyFormValues,
	pricingPolicyId: string
): CreatePricingPolicyInput => ({
	pricingPolicyId,
	name: sanitiseName(values.name),
	priority: parseRequiredInteger(values.priority, 'Priority'),
	isActive: values.isActive,
	scenarioId: values.scenarioId || undefined,
	tariffId: values.tariffId,
	overstayTariffId: values.overstayTariffId
		? values.overstayTariffId
		: undefined,
	limitId: values.limitId || undefined,
	billingCycleId: parseRequiredInteger(values.billingCycleId, 'Billing cycle')
});

export const buildUpdatePricingPolicyInput = (
	values: PricingPolicyFormValues,
	pricingPolicyId: string
): UpdatePricingPolicyInput => ({
	pricingPolicyId,
	name: sanitiseName(values.name),
	priority: parseRequiredInteger(values.priority, 'Priority'),
	isActive: values.isActive,
	scenarioId: values.scenarioId || undefined,
	tariffId: values.tariffId,
	overstayTariffId: values.overstayTariffId
		? values.overstayTariffId
		: undefined,
	limitId: values.limitId || undefined,
	billingCycleId: parseRequiredInteger(values.billingCycleId, 'Billing cycle')
});
